import React, { useMemo, useState } from "react";
import { FaCloudDownloadAlt, FaDatabase, FaExclamationTriangle } from "react-icons/fa";
import { API_ENDPOINTS } from "../config/constants";

const CACHE_RESPONSE_HEADER = "x-billsarthi-desktop-cache";
const MAX_DAYS = 31;

const toYmd = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const parseYmd = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const daysInRange = (fromDate, toDate) => {
  const start = parseYmd(fromDate);
  const end = parseYmd(toDate);
  if (!start || !end || start > end) return [];
  const days = [];
  const cursor = new Date(start);
  while (cursor <= end && days.length <= MAX_DAYS) {
    days.push(toYmd(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const metadataKey = (restaurantId) => `billsarthi:offline-sales-sync:${restaurantId}`;

const readMetadata = (restaurantId) => {
  try {
    return JSON.parse(localStorage.getItem(metadataKey(restaurantId)) || "null");
  } catch {
    return null;
  }
};

const detailHasItems = (detail) => {
  const order = detail?.orderdetails || detail?.order || {};
  return [
    detail?.itemslist,
    detail?.itemlist,
    detail?.orderitems,
    detail?.items,
    order?.itemslist,
    order?.itemlist,
    order?.orderitems,
    order?.items,
  ].some((items) => Array.isArray(items) && items.length > 0);
};

const cacheOrderDetails = async (restaurantId, orders = []) => {
  if (!window.desktopOrderDetails) return;
  const ids = [...new Set(
    orders
      .map((order) => order?.id ?? order?.orderid ?? order?.order_id)
      .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
      .map((id) => String(id))
  )];

  for (let index = 0; index < ids.length; index += 3) {
    const results = await Promise.allSettled(ids.slice(index, index + 3).map(async (orderId) => {
      const existing = await window.desktopOrderDetails.get({ restaurantId, orderId });
      if (detailHasItems(existing)) return;
      const response = await fetch(API_ENDPOINTS.GET_ORDER_DETAIL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId, orderid: orderId }),
      });
      if (!response.ok) throw new Error(`Unable to save bill detail for order ${orderId}.`);
      // electronApiCache stores this successful response in SQLite.
    }));
    results
      .filter((result) => result.status === "rejected")
      .forEach((result) => console.warn("Unable to cache historical bill detail", result.reason));
  }
};

const OfflineSalesSyncPage = () => {
  const today = useMemo(() => toYmd(new Date()), []);
  const [userData] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      return stored?.data || stored || {};
    } catch {
      return {};
    }
  });
  const restaurantId = userData?.id;
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0, date: "" });
  const [result, setResult] = useState(() => readMetadata(restaurantId));
  const [error, setError] = useState("");

  const isDesktop = typeof window !== "undefined" && window.desktopRuntime?.isElectron === true;

  const startSync = async () => {
    setError("");
    if (!isDesktop || !window.desktopOrders) {
      setError("This tool is available only in the BillsArthi desktop app.");
      return;
    }
    if (!restaurantId) {
      setError("Restaurant session not found. Please log in again.");
      return;
    }
    if (navigator.onLine === false) {
      setError("Connect to the internet first. This tool imports live sales into offline SQLite storage.");
      return;
    }

    const dates = daysInRange(fromDate, toDate);
    if (!dates.length) {
      setError("Choose a valid start and end date.");
      return;
    }
    if (dates.length > MAX_DAYS) {
      setError("You can sync a maximum of 31 days (one month) at a time.");
      return;
    }

    setSyncing(true);
    setProgress({ completed: 0, total: dates.length, date: dates[0] });
    let importedOrders = 0;
    const completedDates = [];

    try {
      for (let index = 0; index < dates.length; index += 1) {
        const date = dates[index];
        setProgress({ completed: index, total: dates.length, date });
        const response = await fetch(API_ENDPOINTS.GET_ORDER_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, fromdate: date, todate: date }),
        });

        if (response.headers.get(CACHE_RESPONSE_HEADER)) {
          throw new Error(`Live sales could not be fetched for ${date}; cached data was returned instead.`);
        }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.message || `Unable to fetch sales for ${date}.`);
        }

        const liveOrders = (Array.isArray(payload?.list) ? payload.list : []).filter(
          (order) => !order?.is_pending_sync
        );
        await window.desktopOrders.upsert({ restaurantId, orders: liveOrders });
        await cacheOrderDetails(restaurantId, liveOrders);
        importedOrders += liveOrders.length;
        completedDates.push(date);
        setProgress({ completed: index + 1, total: dates.length, date });
      }

      const summary = {
        fromDate,
        toDate,
        completedDates,
        importedOrders,
        syncedAt: new Date().toISOString(),
      };
      localStorage.setItem(metadataKey(restaurantId), JSON.stringify(summary));
      setResult(summary);
    } catch (syncError) {
      setError(syncError?.message || "Sales sync stopped before completion.");
      if (completedDates.length) {
        const partial = {
          fromDate,
          toDate: completedDates[completedDates.length - 1],
          completedDates,
          importedOrders,
          syncedAt: new Date().toISOString(),
          partial: true,
        };
        localStorage.setItem(metadataKey(restaurantId), JSON.stringify(partial));
        setResult(partial);
      }
    } finally {
      setSyncing(false);
    }
  };

  const percent = progress.total ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <main style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.icon}><FaDatabase /></div>
        <div>
          <div style={styles.kicker}>DESKTOP OFFLINE DATA</div>
          <h1 style={styles.title}>Offline Sales Sync</h1>
          <p style={styles.sub}>Import live sales date-by-date into this desktop app’s SQLite storage.</p>
        </div>
      </section>

      {!isDesktop ? <div style={styles.warning}><FaExclamationTriangle /> This page works only in the Electron desktop app.</div> : null}

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Select sales range</h2>
        <p style={styles.copy}>Maximum 31 days. Each date is fetched through the same Dashboard sales API and saved for offline Dashboard/Sales use.</p>
        <div style={styles.fields}>
          <label style={styles.label}>From date<input type="date" value={fromDate} max={today} disabled={syncing} onChange={(event) => setFromDate(event.target.value)} style={styles.input} /></label>
          <label style={styles.label}>To date<input type="date" value={toDate} max={today} disabled={syncing} onChange={(event) => setToDate(event.target.value)} style={styles.input} /></label>
        </div>
        <button type="button" disabled={syncing || !isDesktop} onClick={startSync} style={{ ...styles.button, ...(syncing || !isDesktop ? styles.buttonDisabled : {}) }}>
          <FaCloudDownloadAlt /> {syncing ? `Fetching ${progress.date} (${progress.completed}/${progress.total})` : "Fetch & save offline sales"}
        </button>
        {syncing ? <div style={styles.progressWrap}><div style={{ ...styles.progressBar, width: `${percent}%` }} /><span>{percent}% complete — {progress.date}</span></div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}
      </section>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Saved offline range</h2>
        {result?.fromDate ? (
          <div style={styles.result}>
            <div><span>Fetched from</span><strong>{result.fromDate}</strong></div>
            <div><span>Fetched until</span><strong>{result.toDate}</strong></div>
            <div><span>Sales saved</span><strong>{result.importedOrders || 0}</strong></div>
            <div><span>Status</span><strong style={{ color: result.partial ? "#b45309" : "#15803d" }}>{result.partial ? "Partial range" : "Complete"}</strong></div>
            <p style={styles.savedAt}>Last sync: {new Date(result.syncedAt).toLocaleString("en-IN")}</p>
          </div>
        ) : <p style={styles.copy}>No manual historical sales range has been saved on this desktop app yet.</p>}
      </section>
    </main>
  );
};

const styles = {
  page: { maxWidth: 980, margin: "0 auto", padding: "26px 18px 44px", color: "#10233b" },
  hero: { display: "flex", gap: 16, alignItems: "center", marginBottom: 22 },
  icon: { width: 50, height: 50, borderRadius: 15, display: "grid", placeItems: "center", background: "var(--app-primary-soft)", color: "var(--app-primary)", fontSize: 22 },
  kicker: { fontSize: 11, letterSpacing: 1.1, fontWeight: 800, color: "var(--app-primary)" },
  title: { margin: "3px 0", fontSize: 27 },
  sub: { margin: 0, color: "#64748b" },
  card: { marginTop: 16, background: "#fff", border: "1px solid #dbe5ef", borderRadius: 16, padding: 22, boxShadow: "0 8px 28px rgba(15,23,42,.05)" },
  cardTitle: { margin: "0 0 7px", fontSize: 18 },
  copy: { margin: "0 0 18px", color: "#64748b", fontSize: 14, lineHeight: 1.5 },
  fields: { display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 },
  label: { display: "grid", gap: 7, color: "#334155", fontWeight: 700, fontSize: 13 },
  input: { minWidth: 210, padding: "10px 11px", border: "1px solid #cbd5e1", borderRadius: 9, color: "#10233b", background: "#fff" },
  button: { border: 0, borderRadius: 9, background: "var(--app-primary)", color: "#fff", padding: "11px 15px", fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
  progressWrap: { marginTop: 17, height: 30, position: "relative", overflow: "hidden", borderRadius: 8, background: "#e2e8f0", color: "#0f172a", fontSize: 12, fontWeight: 700 },
  progressBar: { position: "absolute", inset: "0 auto 0 0", background: "#86efac", transition: "width .2s" },
  error: { marginTop: 15, padding: 12, borderRadius: 9, color: "#b91c1c", background: "#fef2f2", fontWeight: 600, fontSize: 14 },
  warning: { padding: 13, display: "flex", gap: 8, color: "#92400e", background: "#fffbeb", borderRadius: 10 },
  result: { display: "flex", gap: 12, flexWrap: "wrap" },
  savedAt: { width: "100%", margin: "8px 0 0", color: "#64748b", fontSize: 13 },
};

export default OfflineSalesSyncPage;
